import { Controller } from '@hotwired/stimulus'

export default class FormInputsGroupValidatorController extends Controller {
  static targets = ['submitButton', 'inputsContainer']

  connect () {
    // `validate`, not just `updateSelectedStates`. The submit button ships disabled and only a
    // click on an input used to re-enable it, which was fine when every group hardcoded a
    // minimum of 1 and nothing was pre-checked. Package choices break both: an option can be
    // `preselected`, and a question can be genuinely optional, so a form that arrives already
    // satisfied would stay greyed out with nothing for the guest to click.
    this.validate()
  }

  validate = () => {
    this.updateSelectedStates()

    if (!this.hasInputsContainerTarget) return this.disableSubmitButton()
    if (this.allInputsContainersValid()) return this.enableSubmitButton()

    this.disableSubmitButton()
  }

  updateSelectedStates = () => {
    this.inputsContainerTargets.forEach((container) => {
      container.querySelectorAll('.radio-option').forEach((option) => {
        const radio = option.querySelector('input[type=radio]')
        const label = option.querySelector('label')
        if (!radio || !label) return

        if (radio.checked) {
          label.dataset.selected = ''
        } else {
          delete label.dataset.selected
        }
      })
    })
  }

  allInputsContainersValid = () => {
    return this.inputsContainerTargets.reduce((acc, container) => {
      return this.validInputContainer(container) && acc
    }, true)
  }

  validInputContainer = (container) => {
    const validationType = container.dataset.validationType
    // NOT `|| 1`: parseInt('0') is 0, which is falsy, so an OPTIONAL group would demand an
    // answer. Every caller before the package-choices form hardcoded 1, so nothing had ever
    // passed a real zero.
    const parsedMinimum = parseInt(container.dataset.minimumValue)
    const minimumValue = Number.isNaN(parsedMinimum) ? 1 : parsedMinimum
    const maximumValue = parseInt(container.dataset.maximumValue) || null
    const calculator = this._currentValueCalculators()[validationType]
    if (!calculator) return false

    const currentValue = calculator(container)

    return currentValue >= minimumValue && (!maximumValue || currentValue <= maximumValue)
  }

  _currentValueCalculators = () => {
    return {
      radio: this.radioButtonsCurrentValueCalculator,
      checkbox: this.checkBoxesCurrentValueCalculator,
      numeric: this.numericCurrentValueCalculator
    }
  }

  radioButtonsCurrentValueCalculator = (container) => {
    return (container.querySelectorAll('input[type=radio]:checked') || []).length
  }

  // A multi-select question. Without this entry the lookup returns undefined and
  // `validInputContainer` bails to false, which disables submit forever — and, because
  // `allInputsContainersValid` reduces with &&, poisons every other group on the page.
  checkBoxesCurrentValueCalculator = (container) => {
    return (container.querySelectorAll('input[type=checkbox]:checked') || []).length
  }

  numericCurrentValueCalculator = (container) => {
    let currentValue = 0;

    (container.querySelectorAll('input[type=number]') || []).forEach((input) => {
      currentValue += parseInt(input.value) || 0
    })

    return currentValue
  }

  enableSubmitButton = () => {
    if (!this.hasSubmitButtonTarget) return

    this.submitButtonTarget.disabled = false
  }

  disableSubmitButton = () => {
    if (!this.hasSubmitButtonTarget) return

    this.submitButtonTarget.disabled = true
  }
}
